import React, { useRef, useState } from 'react';
import { CONTACT_EMAIL, type SchoolConfig } from '../../content/schools';
import {
  submitBetaApplication,
  trackOutreachEvent,
  type BetaApplicationInput,
  type OutreachAttribution,
} from '../../utils/outreachApi';
import {
  BETA_CONSENT_LABEL,
  BUSINESS_STAGES,
  EMAIL_PATTERN,
  GRADUATION_TIMELINES,
  SPECIALTIES,
} from './formOptions';
import {
  ConsentField,
  HoneypotField,
  RadioGroupField,
  SelectField,
  SubmitError,
  TextAreaField,
  TextField,
  focusFirstError,
  type FormStatus,
} from './fields';

type Errors = Partial<Record<keyof BetaApplicationInput, string>>;

function validate(v: BetaApplicationInput): Errors {
  const errors: Errors = {};
  if (!v.firstName.trim()) errors.firstName = 'First name is required.';
  if (!v.email.trim()) errors.email = 'Email is required.';
  else if (!EMAIL_PATTERN.test(v.email.trim())) errors.email = 'Enter a valid email address.';
  if (!v.specialty) errors.specialty = 'Choose your beauty specialty.';
  if (!v.businessStage) errors.businessStage = 'Choose the option that best describes you.';
  if (!v.graduationTimeline) errors.graduationTimeline = 'Choose your graduation timeline.';
  if (!v.consentToContact) errors.consentToContact = 'Please confirm we can contact you about the beta.';
  return errors;
}

export function BetaApplicationForm({
  school,
  attribution,
}: {
  school: SchoolConfig;
  attribution: OutreachAttribution;
}) {
  const [values, setValues] = useState<BetaApplicationInput>({
    firstName: '',
    email: '',
    specialty: '',
    businessStage: '',
    graduationTimeline: '',
    schoolOrProgram: school.name,
    mainChallenge: '',
    consentToContact: false,
    hfsCheck: '',
  });
  const [errors, setErrors] = useState<Errors>({});
  const [status, setStatus] = useState<FormStatus>('idle');
  const [failure, setFailure] = useState<string>('error');
  const started = useRef(false);
  const formRef = useRef<HTMLFormElement>(null);
  const successRef = useRef<HTMLDivElement>(null);

  function set<K extends keyof BetaApplicationInput>(key: K, value: BetaApplicationInput[K]) {
    if (!started.current) {
      started.current = true;
      trackOutreachEvent('student_application_start', school.slug);
    }
    setValues(prev => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors(prev => ({ ...prev, [key]: undefined }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (status === 'submitting') return;

    const clientErrors = validate(values);
    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors);
      setFailure('validation');
      setStatus('error');
      focusFirstError(formRef.current, clientErrors as Record<string, string>);
      return;
    }

    setStatus('submitting');
    const result = await submitBetaApplication(values, attribution);
    if (result.ok) {
      setStatus('success');
      requestAnimationFrame(() => successRef.current?.focus());
      return;
    }
    if (result.fieldErrors) {
      setErrors(result.fieldErrors as Errors);
      focusFirstError(formRef.current, result.fieldErrors);
    }
    setFailure(result.reason);
    setStatus('error');
  }

  if (status === 'success') {
    return (
      <div ref={successRef} tabIndex={-1} className="sl-alert sl-alert-success" role="status">
        <h3 className="sl-alert-title">Thanks, {values.firstName.trim()}—your application is in.</h3>
        <p>
          We’ll be in touch at <strong>{values.email.trim()}</strong> about next steps for the beta.
          We’ll only use your details for this program—you won’t be added to any marketing list.
        </p>
      </div>
    );
  }

  const submitting = status === 'submitting';

  return (
    <form ref={formRef} className="sl-form" onSubmit={handleSubmit} noValidate aria-busy={submitting}>
      <p className="sl-form-note">Fields marked <span className="sl-req">*</span> are required.</p>

      <div className="sl-form-row">
        <TextField
          id="firstName" label="First name" required autoComplete="given-name" maxLength={80}
          value={values.firstName} onChange={v => set('firstName', v)} error={errors.firstName}
        />
        <TextField
          id="email" label="Email" type="email" required autoComplete="email" inputMode="email" maxLength={254}
          value={values.email} onChange={v => set('email', v)} error={errors.email}
        />
      </div>

      <div className="sl-form-row">
        <SelectField
          id="specialty" label="Beauty specialty" required options={SPECIALTIES}
          value={values.specialty} onChange={v => set('specialty', v)} error={errors.specialty}
        />
        <SelectField
          id="graduationTimeline" label="Expected graduation" required options={GRADUATION_TIMELINES}
          value={values.graduationTimeline} onChange={v => set('graduationTimeline', v)}
          error={errors.graduationTimeline}
        />
      </div>

      <RadioGroupField
        id="businessStage" label="Where are you right now?" required options={BUSINESS_STAGES}
        value={values.businessStage} onChange={v => set('businessStage', v)} error={errors.businessStage}
      />

      <TextField
        id="schoolOrProgram" label="School or program" maxLength={150}
        value={values.schoolOrProgram} onChange={v => set('schoolOrProgram', v)} error={errors.schoolOrProgram}
      />

      <TextAreaField
        id="mainChallenge" label="What’s your biggest business challenge right now?" maxLength={1000}
        hint="For example: keeping track of bookings, remembering client preferences, getting clients to rebook."
        value={values.mainChallenge} onChange={v => set('mainChallenge', v)} error={errors.mainChallenge}
      />

      <ConsentField
        id="consentToContact" label={BETA_CONSENT_LABEL}
        checked={values.consentToContact} onChange={v => set('consentToContact', v)}
        error={errors.consentToContact}
      />

      <HoneypotField id="beta_hfs_check" value={values.hfsCheck} onChange={v => setValues(prev => ({ ...prev, hfsCheck: v }))} />

      {status === 'error' && <SubmitError reason={failure} contactEmail={CONTACT_EMAIL} />}

      <button type="submit" className="sl-btn sl-btn-primary sl-btn-block" disabled={submitting}>
        {submitting ? 'Submitting…' : 'Submit application'}
      </button>
      <p className="sl-fine">
        We’ll only use this to contact you about the Help from Sky beta. We won’t add you to a marketing
        list or share your details. This site is protected by reCAPTCHA.
      </p>
    </form>
  );
}
